
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getProfile } from '@/services/profileService';
import { Profile, AuthContextType } from '@/types/auth';
import * as authService from '@/services/authService';
import { isUserAdmin, isPlatformAdmin, isOrgAdmin } from '@/utils/authUtils';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfileWithRetry = async (userId: string, retries = 3): Promise<Profile | null> => {
    for (let i = 0; i < retries; i++) {
      try {
        const profileData = await getProfile(userId);
        if (profileData) {
          const typedProfile: Profile = {
            ...profileData,
            user_type: profileData.user_type as 'platform_admin' | 'org_admin' | 'team_member'
          };
          return typedProfile;
        }
      } catch (error) {
        if (i === retries - 1) {
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, 500 * (i + 1)));
      }
    }
    return null;
  };

  useEffect(() => {
    let isProcessing = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (isProcessing) return;

      if (session?.user) {
        setLoading(true);
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (!session?.user) {
        setProfile(null);
        setLoading(false);
        return;
      }

      isProcessing = true;

      const userId = session.user.id;
      setTimeout(() => {
        if (event === 'SIGNED_IN') {
          authService.updateInvitationStatusOnSignIn(userId).catch(() => {});
        }

        fetchProfileWithRetry(userId)
          .then(profile => {
            setProfile(profile);
          })
          .catch(() => {
            setProfile(null);
          })
          .finally(() => {
            setLoading(false);
            isProcessing = false;
          });
      }, 0);
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isProcessing) return;

      setSession(session);
      setUser(session?.user ?? null);

      if (!session?.user) {
        setLoading(false);
        return;
      }

      isProcessing = true;
      setLoading(true);

      const userId = session.user.id;
      setTimeout(() => {
        fetchProfileWithRetry(userId)
          .then(profile => {
            setProfile(profile);
          })
          .catch(() => {
            setProfile(null);
          })
          .finally(() => {
            setLoading(false);
            isProcessing = false;
          });
      }, 0);
    });

    return () => {
      subscription.unsubscribe();
      isProcessing = false;
    };
  }, []);

  const handleSignOut = async () => {
    // Clear local state immediately to prevent race conditions
    setUser(null);
    setProfile(null);
    setSession(null);
    
    // The authService.signOut will handle the actual logout and redirection
    await authService.signOut();
  };

  const handleUpdateProfile = async (updates: { full_name?: string }) => {
    if (!user) {
      return { error: { message: 'User not authenticated' } };
    }

    const result = await authService.updateProfile(user.id, updates);
    
    if (!result.error && result.profile) {
      // Type cast user_type to ensure it matches our union type
      const typedProfile: Profile = {
        ...result.profile,
        user_type: result.profile.user_type as 'platform_admin' | 'org_admin' | 'team_member'
      };
      setProfile(typedProfile);
    }

    return { error: result.error };
  };

  const isAdmin = isUserAdmin(profile);
  const isPlatformAdminUser = isPlatformAdmin(profile);
  const isOrgAdminUser = isOrgAdmin(profile);

  const value = {
    user,
    profile,
    session,
    loading,
    signIn: authService.signIn,
    signOut: handleSignOut,
    resetPassword: authService.resetPassword,
    changePassword: authService.changePassword,
    updateProfile: handleUpdateProfile,
    isAdmin,
    isPlatformAdmin: isPlatformAdminUser,
    isOrgAdmin: isOrgAdminUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
